import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

const services = [
  {
    id: 'manufacturing',
    title: 'Couch Manufacturing',
    description: 'Custom-built couches made to order with premium materials and expert craftsmanship. Design your dream couch from scratch.',
    icon: 'fa-industry',
    image: 'https://images.unsplash.com/photo-1564842505181-8862a3b9b173?w=600&q=80',
    features: ['Custom dimensions', 'Material selection', 'Color matching'],
    color: 'from-leather/80 to-leather'
  },
  {
    id: 'sales',
    title: 'Couch Sales',
    description: 'Ready-made luxury couches for homes and offices. Browse our extensive collection of premium furniture pieces.',
    icon: 'fa-shopping-bag',
    image: 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=600&q=80',
    features: ['Wide selection', 'Immediate delivery', 'Finance options'],
    color: 'from-gold/80 to-gold'
  },
  {
    id: 'reupholstery',
    title: 'Couch Reupholstery',
    description: 'Refurbish, redesign and restore your old couches to like-new condition. Give your furniture a second life.',
    icon: 'fa-magic',
    image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&q=80',
    features: ['Fabric replacement', 'Foam renewal', 'Frame repair'],
    color: 'from-charcoal/80 to-charcoal'
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
};

export const ServicesSection = () => {
  return (
    <section data-testid="services-section" className="section-padding bg-cream texture-overlay">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-gold font-body text-sm uppercase tracking-widest">What We Offer</span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-leather mt-2 mb-4">
            Our Premium Services
          </h2>
          <p className="text-leather/60 font-body max-w-2xl mx-auto">
            From custom manufacturing to expert reupholstery, we provide comprehensive couch solutions tailored to your needs.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service) => (
            <motion.div key={service.id} variants={itemVariants}>
              <Card 
                data-testid={`service-card-${service.id}`}
                className="group overflow-hidden border-0 shadow-card hover:shadow-card-hover transition-all duration-500 hover:-translate-y-2 bg-white"
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-60`}></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <i className={`fas ${service.icon} text-2xl text-white`}></i>
                    </div>
                  </div>
                </div>

                <CardContent className="p-6">
                  <h3 className="font-heading text-xl text-leather font-semibold mb-3">
                    {service.title}
                  </h3>
                  <p className="text-leather/60 font-body text-sm mb-4 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-leather/70">
                        <i className="fas fa-check text-gold text-xs"></i>
                        <span className="font-body">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to={`/services#${service.id}`}>
                    <Button 
                      variant="outline"
                      data-testid={`service-btn-${service.id}`}
                      className="w-full rounded-full border-leather text-leather hover:bg-leather hover:text-white transition-all"
                    >
                      Learn More
                      <i className="fas fa-arrow-right ml-2 text-sm"></i>
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <Link to="/quote">
            <Button 
              data-testid="services-get-quote-btn"
              className="btn-gold rounded-full px-10 py-4 text-base"
            >
              Get a Free Quote
              <i className="fas fa-arrow-right ml-3"></i>
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
